import type {
  GroupJoinRequest,
  CreateGroupInput,
  Conversation,
  GroupSearchQuery,
  GroupSummary,
  MediaAsset,
  Message,
  Page,
  RealtimeTicketResponse,
  UpdateGroupInput,
} from '@talkvibe/shared';
import { api } from './api.ts';

export const chatService = {
  /**
   * The inbox, in one of its two halves.
   *
   * `scope` is sent rather than filtering what comes back, because the two
   * lists paginate separately: a cursor walks one ordering, and asking for
   * twenty conversations and discarding the marketplace ones gives a short
   * page and a cursor that has already stepped past the rest.
   */
  conversations: (cursor?: string, scope?: 'chats' | 'listings'): Promise<Page<Conversation>> =>
    api.get<Page<Conversation>>('/api/conversations', { query: { cursor, scope } }),

  conversation: (id: string): Promise<Conversation> =>
    api.get<Conversation>(`/api/conversations/${encodeURIComponent(id)}`),

  /** Opens or reuses the 1:1 conversation with another user. */
  createConversation: (userId: string): Promise<Conversation> =>
    api.post<Conversation>('/api/conversations', { userId }),

  /**
   * Opens or reuses the conversation with a seller about one item.
   *
   * A different field on the same endpoint, not a different endpoint, because
   * the two rules belong where they can be read together: `userId` reaches
   * somebody you are connected to and `listingId` reaches a seller you are
   * not. This is the only way in the product to start a conversation with a
   * stranger, and the server draws that exception narrowly — the listing must
   * be active and must not be yours, and a block still ends it.
   *
   * The same item always answers with the same thread.
   */
  createListingConversation: (listingId: string): Promise<Conversation> =>
    api.post<Conversation>('/api/conversations', { listingId }),

  /** Newest first; pass the previous page's cursor to walk back through history. */
  messages: (conversationId: string, cursor?: string, limit?: number): Promise<Page<Message>> =>
    api.get<Page<Message>>(`/api/conversations/${encodeURIComponent(conversationId)}/messages`, {
      query: { cursor, limit },
    }),

  /** HTTP fallback for sending, used when the socket is not connected. */
  sendMessage: (
    conversationId: string,
    content: string,
    mediaId?: string,
    replyToId?: string,
  ): Promise<Message> =>
    api.post<Message>('/api/messages', {
      conversationId,
      content,
      ...(mediaId ? { mediaId } : {}),
      ...(replyToId ? { replyToId } : {}),
    }),

  /** Removes one of your own messages. The row stays; its contents do not. */
  deleteMessage: (messageId: string): Promise<Message> =>
    api.delete<Message>(`/api/messages/${encodeURIComponent(messageId)}`),

  /**
   * Likes a message, or unlikes it.
   *
   * Answers with the count, not the message: that is the only thing anybody else
   * is shown, and every other field is either per-viewer or unchanged.
   */
  likeMessage: (messageId: string): Promise<{ messageId: string; likeCount: number }> =>
    api.put(`/api/messages/${encodeURIComponent(messageId)}/like`),
  unlikeMessage: (messageId: string): Promise<{ messageId: string; likeCount: number }> =>
    api.delete(`/api/messages/${encodeURIComponent(messageId)}/like`),

  /**
   * Uploads a photo to send in a message, and returns the asset id.
   *
   * Its own kind, `message`: the kind decides the R2 prefix and is what the send
   * path checks before letting an id be attached, so a story or a cover cannot be
   * sent as a chat photo.
   */
  uploadMessageImage: (file: File): Promise<{ id: string; url: string }> => {
    const form = new FormData();
    form.append('file', file);
    return api.post<{ id: string; url: string }>('/api/me/message-image', form);
  },

  /**
   * Sends a recording and gets back the asset it became.
   *
   * `status` comes back `pending`: the bytes are stored, and the copy every
   * browser can play is made out of band. The message can be sent straight
   * away — the bubble shows the recording as still arriving until the
   * conversion lands.
   */
  uploadVoiceNote: (file: Blob): Promise<{ id: string; url: string; status: string }> => {
    const form = new FormData();
    // A name is required by the form encoding and is never used by the server,
    // which builds its own key — but it must carry an extension the server's
    // magic-byte check will agree with, for the sake of anything in between
    // that sniffs on filename.
    form.append('file', file, 'voice-note');
    return api.post<{ id: string; url: string; status: string }>('/api/me/message-voice', form);
  },

  /**
   * One asset's current state.
   *
   * Only voice messages ask for this, and only while a recording is still
   * being converted: a photo is useful the moment it is stored, but a
   * recording's original is whatever the sender's browser produced — the WebM
   * no iPhone can play — so its bubble refuses to play anything until the
   * converted copy exists, and needs a way to find out when it does.
   */
  asset: (id: string): Promise<MediaAsset> =>
    api.get<MediaAsset>(`/api/media/${encodeURIComponent(id)}`),

  markRead: (conversationId: string, messageId: string): Promise<void> =>
    api
      .post(`/api/conversations/${encodeURIComponent(conversationId)}/read`, { messageId })
      .then(() => undefined),

  realtimeTicket: (conversationId: string): Promise<RealtimeTicketResponse> =>
    api.post<RealtimeTicketResponse>('/api/realtime/ticket', { conversationId }),

  /* ---- Groups ---- */

  createGroup: (input: CreateGroupInput): Promise<Conversation> =>
    api.post<Conversation>('/api/groups', input),

  addGroupMembers: (conversationId: string, userIds: string[]): Promise<Conversation> =>
    api.post<Conversation>(`/api/groups/${conversationId}/members`, { userIds }),

  /**
   * Joins a findable group, or asks to.
   *
   * `joined` says which it did, so the button knows what it has become without a
   * second request — an `approval` group answers false and the person is in the queue.
   */
  joinGroup: (conversationId: string): Promise<{ joined: boolean }> =>
    api.post<{ joined: boolean }>(`/api/groups/${conversationId}/join`),

  cancelJoinRequest: (conversationId: string): Promise<void> =>
    api.delete<void>(`/api/groups/${conversationId}/join`),

  /** The queue. Organisers only; the API answers 403 to anybody else. */
  joinRequests: (conversationId: string): Promise<{ items: GroupJoinRequest[] }> =>
    api.get<{ items: GroupJoinRequest[] }>(`/api/groups/${conversationId}/requests`),

  approveJoinRequest: (conversationId: string, userId: string): Promise<Conversation> =>
    api.post<Conversation>(`/api/groups/${conversationId}/requests/${userId}`),

  declineJoinRequest: (conversationId: string, userId: string): Promise<void> =>
    api.delete<void>(`/api/groups/${conversationId}/requests/${userId}`),

  removeGroupMember: (conversationId: string, userId: string): Promise<void> =>
    api.delete<void>(`/api/groups/${conversationId}/members/${userId}`),

  leaveGroup: (conversationId: string): Promise<void> =>
    api.post<void>(`/api/groups/${conversationId}/leave`),

  renameGroup: (conversationId: string, name: string): Promise<Conversation> =>
    api.patch<Conversation>(`/api/groups/${conversationId}`, { name }),

  /** Only the fields present are written; the rest are left alone. */
  updateGroup: (conversationId: string, input: UpdateGroupInput): Promise<Conversation> =>
    api.patch<Conversation>(`/api/groups/${conversationId}`, input),

  /**
   * The groups you are in, of both kinds.
   *
   * Not the inbox: that leaves post groups out on purpose, so reading it for this would
   * make joining a post group look like leaving it.
   */
  myGroups: (): Promise<{ items: GroupSummary[] }> =>
    api.get<{ items: GroupSummary[] }>('/api/groups/mine'),

  /** Groups anybody can find. Summaries only — no members, no posts. */
  searchGroups: (query: GroupSearchQuery = {}): Promise<Page<GroupSummary>> => {
    const params = new URLSearchParams();
    if (query.category) params.set('category', query.category);
    if (query.tag) params.set('tag', query.tag);
    if (query.q) params.set('q', query.q);
    if (query.cursor) params.set('cursor', query.cursor);
    if (query.limit) params.set('limit', String(query.limit));
    const suffix = params.toString();
    return api.get<Page<GroupSummary>>(`/api/groups${suffix ? `?${suffix}` : ''}`);
  },

  /** A group's header, readable without being a member when it allows it. */
  group: (conversationId: string): Promise<GroupSummary> =>
    api.get<GroupSummary>(`/api/groups/${conversationId}`),

  uploadGroupCover: (conversationId: string, file: File): Promise<Conversation> => {
    const body = new FormData();
    body.append('file', file);
    // `api.post` passes a FormData body through without a JSON content type.
    return api.post<Conversation>(`/api/groups/${conversationId}/cover`, body);
  },
};
